import * as Yup from 'yup';
import { Formik, Form } from "formik";
import { Row, Col, Button } from 'antd';
import DateInput from '../inputs/DateInput';
import SelectInput from '../inputs/SelectInput';
import { CiSearch } from 'react-icons/ci';
import { RxReset } from 'react-icons/rx';
import { getData } from '../../helpers/api';
import { useEffect, useState, useRef, useCallback } from 'react';
import moment from 'moment';
import { getLocalStorage } from '../../helpers/localstorage';

const validationSchema = Yup.object({
    userId: Yup.string().nullable().optional(),
    productId: Yup.string().nullable().optional(),
    startDate: Yup.date()
        .nullable()
        .optional()
        .test('isValidDate', 'La date de début n\'est pas valide', (value) => !value || moment(value, 'YYYY-MM-DD', true).isValid()),
    endDate: Yup.date()
        .nullable()
        .optional()
        .test('isValidDate', 'La date de fin n\'est pas valide', (value) => !value || moment(value, 'YYYY-MM-DD', true).isValid())
        .when('startDate', (startDate, schema) =>
            startDate[0]
                ? schema.min(startDate, 'La date de fin doit être postérieure à la date de début')
                : schema
        ),
});

const StatisticsFilter = ({ setFilter, filter }) => {
    const user = getLocalStorage("auth")?.user;
    const [confirmationUsers, setConfirmationUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const formRef = useRef(null);

    const getInitialValues = useCallback(() => {
        const initialValues = {
            userId: null,
            productId: null,
            startDate: null,
            endDate: null
        };

        if (filter) {
            if (filter.dateRange) {
                initialValues.startDate = moment(filter.dateRange[0]).toISOString();
                initialValues.endDate = moment(filter.dateRange[1]).toISOString();
            } else if (filter.createdAt) {
                initialValues.startDate = moment(filter.createdAt).toISOString();
            }
            if (filter.userId) {
                initialValues.userId = filter.userId;
            }
            if (filter.productId) {
                initialValues.productId = filter.productId;
            }
        }

        return initialValues;
    }, [filter]);

    const getConfirmationUsers = async () => {
        try {
            const res = await getData('/users/user/subordinated');
            setConfirmationUsers(res.data.map((user) => ({ 
                value: user.id, 
                label: user.name 
            })));
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
        }
    };

    const getProducts = async () => {
        try {
            const res = await getData("/products");
            setProducts(res.data.map((product) => ({ 
                value: product.id, 
                label: product.name 
            })));
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
        }
    };

    useEffect(() => {
        getConfirmationUsers();
        getProducts();
    }, []);

    useEffect(() => {
        if (formRef.current && filter) {
            formRef.current.setValues(getInitialValues());
        }
    }, [filter, getInitialValues]);

    const handleSubmit = (values) => {
        const newFilter = {};
        if (values.startDate && values.endDate) {
            newFilter.dateRange = [
                moment(values.startDate).format('YYYY-MM-DD'),
                moment(values.endDate).format('YYYY-MM-DD')
            ];
        } else if (values.startDate) {
            newFilter.createdAt = moment(values.startDate).format('YYYY-MM-DD');
        }
        
        if (values.userId) {
            newFilter.userId = values.userId;
        }
        if (values.productId) {
            newFilter.productId = values.productId;
        }
        
        setFilter(newFilter);
    };

    const handleReset = () => {
        if (formRef.current) {
            formRef.current.resetForm();
            setFilter({});
        }
    };

    return (
        <Formik
            innerRef={formRef}
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ isValid, dirty }) => (
                <Form>
                    <Row gutter={[16, 16]}>
                        {['Gestion', 'super admin'].includes(user?.role) && (
                            <Col xs={24} md={6} lg={4}>
                                <SelectInput
                                    name="userId"
                                    placeholder="Employés"
                                    options={confirmationUsers}
                                />
                            </Col>
                        )}
                        <Col xs={24} md={6} lg={4}>
                            <SelectInput
                                name="productId"
                                placeholder="Produits"
                                options={products}
                            />
                        </Col>
                        <Col xs={24} md={6} lg={4}>
                            <DateInput
                                name="startDate"
                                placeholder="Date de début"
                            />
                        </Col>
                        <Col xs={24} md={6} lg={4}>
                            <DateInput
                                name="endDate"
                                placeholder="Date de fin"
                            />
                        </Col>
                        <Col xs={24} md={6} lg={4}>
                            <Button 
                                style={{ marginRight: 5 }} 
                                disabled={!isValid} 
                                size="default" 
                                type="primary" 
                                htmlType="submit"
                            >
                                <CiSearch />
                            </Button>
                            <Button 
                                style={{ marginLeft: 5 }} 
                                size="default" 
                                onClick={handleReset}
                            >
                                <RxReset />
                            </Button>
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

export default StatisticsFilter;